<template>
  <div id="step_modals">
    <CustomModal
      :title="`Delete ${featureNames.SEQUENCES} Step`"
      :visible="deleteModalVisible"
      @update:visible="$emit('update:delete-modal', $event)"
      :width="'32vw'"
    >
      <span>Are you sure you want to delete this step?</span>
      <template #footer>
        <a-button
          class="primary_medium"
          @click="$emit('delete-step')"
          :loading="isLoading"
        >
          Confirm
        </a-button>
      </template>
    </CustomModal>

    <CustomModal
      :title="`Move Step ${step?.position || ''} Up`"
      :visible="moveUpModalVisible"
      @update:visible="$emit('update:move-up-modal', $event)"
      :width="'32vw'"
    >
      <span
        >Are you sure you want to move step {{ step?.position || "" }} up?
        Contacts/Leads who have already completed the previous step will skip
        this step.</span
      >
      <template #footer>
        <a-button
          class="secondary_medium"
          @click="$emit('update:move-up-modal', false)"
          :disabled="isLoading"
          >Cancel</a-button
        >
        <a-button
          class="primary_medium ml-2"
          @click="$emit('move-step-up')"
          :loading="isLoading"
        >
          Move Up
        </a-button>
      </template>
    </CustomModal>

    <CustomModal
      :title="`Move Step ${step?.position || ''} Down`"
      :visible="moveDownModalVisible"
      @update:visible="$emit('update:move-down-modal', $event)"
      :width="'32vw'"
    >
      <span
        >Are you sure you want to move step {{ step?.position || "" }} down?
        Contacts/Leads who have already completed this step will skip the next
        step.</span
      >
      <template #footer>
        <a-button
          class="secondary_medium"
          @click="$emit('update:move-down-modal', false)"
          :disabled="isLoading"
          >Cancel</a-button
        >
        <a-button
          class="primary_medium ml-2"
          @click="$emit('move-step-down')"
          :loading="isLoading"
        >
          Move Down
        </a-button>
      </template>
    </CustomModal>

    <CustomModal
      :title="`Add Step Above`"
      :visible="addAboveModalVisible"
      @update:visible="$emit('update:add-above-modal', $event)"
      :width="'32vw'"
    >
      <span
        >Are you sure you want to add a new step above step
        {{ step?.position || "" }}? Contacts/Leads who have already completed
        this step will skip the above step.</span
      >
      <template #footer>
        <a-button
          class="secondary_medium"
          @click="$emit('update:add-above-modal', false)"
          >Cancel</a-button
        >
        <a-button class="primary_medium ml-2" @click="$emit('add-step-above')"
          >Add Step</a-button
        >
      </template>
    </CustomModal>

    <CustomModal
      :title="`Add Step Below`"
      :visible="addBelowModalVisible"
      @update:visible="$emit('update:add-below-modal', $event)"
      :width="'32vw'"
    >
      <span
        >Are you sure you want to add a new step below step
        {{ step?.position || "" }}? Contacts/Leads who have already completed
        the next step will skip the below step.</span
      >
      <template #footer>
        <a-button
          class="secondary_medium"
          @click="$emit('update:add-below-modal', false)"
          >Cancel</a-button
        >
        <a-button class="primary_medium ml-2" @click="$emit('add-step-below')"
          >Add Step</a-button
        >
      </template>
    </CustomModal>

    <CustomModal
      :title="'Edit Step ' + camelize(step?.step_type || '')"
      :visible="editModalVisible"
      @update:visible="$emit('update:edit-modal', $event)"
      :width="'32vw'"
    >
      <div id="edit_modal_configs">
        <BaseStepConfig
          :model-value="formData"
          :step-type="step?.step_type"
          :is-first-step="step?.position === 1"
          :sequence-id="step?.sequence_id"
          @update:model-value="$emit('update:formData', $event)"
        />

        <QualificationConfig
          v-if="step?.step_type === sequenceStepTypes.QUALIFICATION"
          :model-value="formData"
        />

        <VoiceAgentConfig
          v-if="step?.step_type === sequenceStepTypes.VOICE"
          :model-value="formData"
        />

        <MultiThreadingConfig
          v-if="step?.step_type === sequenceStepTypes.MULTI_THREADING"
          :model-value="formData"
          :job-title-options="jobTitleOptions"
          :seniority-options="seniorityOptions"
          :department-options="departmentOptions"
          :sequences="sequences"
        />

        <TaskConfig
          v-if="step?.automation_type === 'TASK'"
          :model-value="formData"
          @update:model-value="$emit('update:formData', $event)"
        />

        <LinkedinInviteConfig
          v-if="
            step?.step_type === 'LINKEDIN_INVITE' &&
            step?.automation_type !== 'TASK'
          "
          :model-value="formData"
        />

        <!-- <div
          class="flex flex-col gap-2 mt-5"
          v-if="
            ![
              'MARKET_RESEARCH',
              'TASK',
              'QUALIFICATION',
              'VOICE',
              'MULTI_THREADING',
              'NEXT_BEST_ACTION',
              "AUTOMATED"
            ].includes(step?.step_type || step?.automation_type) &&
            formData?.instruction !== undefined
          "
        >
          <span class="secondary_color text-[13px]">Instructions</span>
          <a-textarea
            v-model:value="formData.instruction"
            class="textarea_large"
            :rows="4"
            placeholder="Enter instructions"
          />
        </div> -->
      </div>

      <template #footer>
        <a-button
          class="secondary_medium"
          @click="$emit('update:edit-modal', false)"
        >
          Cancel
        </a-button>
        <a-button class="primary_medium ml-2" @click="$emit('update-step')">
          Update
        </a-button>
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import CustomModal from "@/src/components/CustomModal.vue";
import { sequenceStepTypes } from "@/src/constants/sequenceConstants.js";
import BaseStepConfig from "@/src/components/sequences_components/configs/BaseStepConfig.vue";
import QualificationConfig from "@/src/components/sequences_components/configs/QualificationConfig.vue";
import VoiceAgentConfig from "@/src/components/sequences_components/configs/VoiceAgentConfig.vue";
import MultiThreadingConfig from "@/src/components/sequences_components/configs/MultiThreadingConfig.vue";
import TaskConfig from "@/src/components/sequences_components/configs/TaskConfig.vue";
import LinkedinInviteConfig from "@/src/components/sequences_components/configs/LinkedinInviteConfig.vue";
import { featureNames } from "@/src/constants/globalConstants";

const props = defineProps({
  editModal: Boolean,
  deleteModal: Boolean,
  moveUpModal: Boolean,
  moveDownModal: Boolean,
  addAboveModal: Boolean,
  addBelowModal: Boolean,
  formData: Object,
  step: Object,
  filters: Object,
  sequences: Array,
  totalSteps: {
    type: Number,
    default: 1,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const editModalVisible = computed({
  get: () => props.editModal,
  set: (value) => emit("update:edit-modal", value),
});

const deleteModalVisible = computed({
  get: () => props.deleteModal,
  set: (value) => emit("update:delete-modal", value),
});

const moveUpModalVisible = computed({
  get: () => props.moveUpModal,
  set: (value) => emit("update:move-up-modal", value),
});

const moveDownModalVisible = computed({
  get: () => props.moveDownModal,
  set: (value) => emit("update:move-down-modal", value),
});

const addAboveModalVisible = computed({
  get: () => props.addAboveModal,
  set: (value) => emit("update:add-above-modal", value),
});

const addBelowModalVisible = computed({
  get: () => props.addBelowModal,
  set: (value) => emit("update:add-below-modal", value),
});

const emit = defineEmits([
  "update:edit-modal",
  "update:delete-modal",
  "update:move-up-modal",
  "update:move-down-modal",
  "update:add-above-modal",
  "update:add-below-modal",
  "update:formData",
  "update-step",
  "delete-step",
  "move-step-up",
  "move-step-down",
  "add-step-above",
  "add-step-below",
]);

function camelize(str) {
  return (
    str
      ?.split(/[\s-_]+/)
      ?.map(
        (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
      )
      ?.join(" ") || ""
  );
}

const jobTitleOptions = ref(props.filters?.job_title || []);
const seniorityOptions = ref(props.filters?.seniority || []);
const departmentOptions = ref(props.filters?.department || []);

watch(
  () => props.formData?.delay_time,
  (newVal) => {
    if (newVal < 0) {
      props.formData.delay_time = 0;
    }
  },
);
</script>

<style scoped>
.selected_option {
  background-color: var(--white-opacity-2);
  border: 1px solid var(--white-opacity-8);
  border-radius: 4px;
}
</style>
